import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { useInaccessibleElement, useIsFocused } from '@sg/garnish';

import { Navbar as NavbarComponents } from '@order/navigation';
import { useTelemetry } from '@order/Telemetry';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useNavigateToLastInteractedStore } from '../../context/LastInteractedStore';
import { useBagCount } from '../../hooks/useBagCount';
import { useLoyaltyAvailablePoints } from '../../hooks/useLoyaltyAvailablePoints';

export const Navbar = () => {
  const { track } = useTelemetry();
  const router = useRouter();
  const navigateLastStore = useNavigateToLastInteractedStore();

  // ─── Accessibility ─────────────────────────────────────────────────────────
  // Here we add the "accessibilityHidden" prop when the navbar isn't focused.
  // This is because with react-navigation we'll render this navbar multiple times.

  const isVisible = useIsFocused();
  const a11yProps = useInaccessibleElement(!isVisible);

  // ─── Remote Data ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const points = useLoyaltyAvailablePoints();
  const { bagCount } = useBagCount();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const navigateHome = useCallback(() => {
    track('home.jump_nav');

    router.navigate('/');
  }, [track, router]);

  const navigateLoyalty = useCallback(() => {
    track('loyalty.jump_nav');

    router.navigate('/rewards');
  }, [track, router]);

  const navigateGift = useCallback(() => {
    track('gift.jump_nav');

    router.navigate('/gift');
  }, [track, router]);

  const navigateMenu = useCallback(() => {
    track('menu.jump_nav', {});
    navigateLastStore();
  }, [track, navigateLastStore]);

  const navigateOrder = useCallback(() => {
    track('order.jump_nav');
    navigateLastStore();
  }, [track, navigateLastStore]);

  const navigateBag = useCallback(() => {
    track('bag.jump_nav');

    router.navigate('/bag');
  }, [track, router]);

  const navigateAccount = useCallback(() => {
    track('account.jump_nav');

    router.navigate('/account/menu');
  }, [track, router]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <NavbarComponents.Containers.Main {...a11yProps}>
      <NavbarComponents.Containers.Left>
        <NavbarComponents.Gift onPress={navigateGift} />
        <NavbarComponents.Loyalty
          points={points}
          isLoggedIn={isLoggedIn}
          onPress={navigateLoyalty}
        />
        <NavbarComponents.Menu onPress={navigateMenu} />
      </NavbarComponents.Containers.Left>

      <NavbarComponents.Home onPress={navigateHome} />

      <NavbarComponents.Containers.Right>
        <NavbarComponents.Order onPress={navigateOrder} />
        <NavbarComponents.Bag count={bagCount} onPress={navigateBag} />
        <NavbarComponents.Account onPress={navigateAccount} />
      </NavbarComponents.Containers.Right>
    </NavbarComponents.Containers.Main>
  );
};
